import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addWebSocketMessage as addChatWebSocketMessage } from 'store/chatSlice';
import { addWebSocketMessage as addInboxWebSocketMessage } from 'store/inboxSlice';
import MessageProcessor from './MessageProcessor';
import useSharedWebSocket from './useSharedWebSocket';
/**
 * Generic hook to subscribe to real-time messages via WebSocket
 * Works with both inboxSlice and chatSlice
 */
export const useGenericMessageSubscription = (conversationId, sliceType = 'inbox') => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.data);
    const { subscribe, unsubscribe, sendMessage: sendWebSocketMessage, isConnected, } = useSharedWebSocket();
    const subscriptionSentRef = useRef(false);
    const isSubscribedRef = useRef(false);
    const subscriberId = useRef(`message-subscription-${conversationId || 'default'}-${sliceType}`);
    const messageProcessor = MessageProcessor.getInstance();
    // Update subscriber ID when conversationId changes
    useEffect(() => {
        subscriberId.current = `message-subscription-${conversationId || 'default'}-${sliceType}`;
    }, [conversationId, sliceType]);
    // Subscribe to shared WebSocket
    useEffect(() => {
        if (!conversationId) {
            return undefined;
        }
        // Reset subscription flags when conversationId changes
        isSubscribedRef.current = false;
        subscriptionSentRef.current = false;
        const handleMessage = (data) => {
            if (data.type === 'connection_ack') {
                // Subscribe to messages for this conversation (only once)
                if (conversationId && !subscriptionSentRef.current) {
                    // Add a small delay to ensure the connection is fully established
                    setTimeout(() => {
                        sendWebSocketMessage({
                            type: 'subscribe_messages',
                            id: `msg_sub_${Date.now()}`,
                            payload: {
                                conversationId,
                            },
                        });
                        subscriptionSentRef.current = true;
                    }, 100);
                }
                return;
            }
            if (data.type === 'message_subscription_ack') {
                return;
            }
            if (data.type === 'new_message' && data.payload) {
                const { conversationId: msgConversationId, message } = data.payload;
                // Only process messages for the current conversation
                if (!conversationId || msgConversationId === conversationId) {
                    // Use global message processor to prevent duplicates
                    const wasProcessed = messageProcessor.processMessage(data);
                    if (wasProcessed) {
                        // Determine if the message was sent by the current user
                        const sendByMe = currentUser && message.sender && currentUser.id === message.sender.id;
                        // Add the message to the appropriate Redux store
                        const messageData = {
                            id: message.id,
                            text: message.text,
                            image: message.image || null,
                            createdDate: message.createdDate,
                            sender: message.sender,
                            sendByMe: sendByMe || false,
                            __typename: 'MessageNode',
                        };
                        if (sliceType === 'inbox') {
                            dispatch(addInboxWebSocketMessage(messageData));
                        }
                        else {
                            dispatch(addChatWebSocketMessage(messageData));
                        }
                    }
                }
            }
        };
        const handleConnectionChange = (connected) => {
            if (connected && conversationId) {
                // Always re-subscribe when connection is established (including reconnections)
                // Reset the flag first to allow re-subscription
                subscriptionSentRef.current = false;
                // Send subscription immediately when connected
                setTimeout(() => {
                    if (!subscriptionSentRef.current) {
                        sendWebSocketMessage({
                            type: 'subscribe_messages',
                            id: `msg_sub_${Date.now()}`,
                            payload: {
                                conversationId,
                            },
                        });
                        subscriptionSentRef.current = true;
                    }
                }, 100);
            }
        };
        subscribe({
            id: subscriberId.current,
            onMessage: handleMessage,
            onConnectionChange: handleConnectionChange,
        });
        // Send subscription immediately for new conversation
        if (conversationId && !subscriptionSentRef.current) {
            setTimeout(() => {
                sendWebSocketMessage({
                    type: 'subscribe_messages',
                    id: `msg_sub_${Date.now()}`,
                    payload: {
                        conversationId,
                    },
                });
                subscriptionSentRef.current = true;
            }, 100);
        }
        return () => {
            isSubscribedRef.current = false;
            unsubscribe(subscriberId.current);
        };
    }, [
        conversationId,
        sliceType,
        dispatch,
        subscribe,
        unsubscribe,
        sendWebSocketMessage,
        currentUser,
        messageProcessor,
    ]);
    return {
        isConnected,
        hasWebSocket: Boolean(conversationId),
    };
};
export default useGenericMessageSubscription;
